<template>
  <div v-if="visible" class="f-overlay" @click.self="close">
    <div class="f-modal">
      <!-- Header -->
      <div class="f-header">
        <div class="f-header-left">
          <img v-if="integration?.icon" :src="integration.icon" class="f-logo" />
          <span class="f-title">Connect {{ integration?.name || integrationId }}</span>
        </div>
        <button class="f-close" @click="close">&times;</button>
      </div>

      <!-- Content -->
      <div class="f-body">
        <!-- Completed -->
        <div v-if="state === 'completed'" class="f-center">
          <div class="f-check">&#10003;</div>
          <p class="f-message">Connected successfully</p>
        </div>

        <!-- Error -->
        <div v-else-if="state === 'error'" class="f-center">
          <div class="f-error-icon">!</div>
          <p class="f-message">{{ error }}</p>
        </div>

        <!-- Loading -->
        <div v-else-if="state === 'connecting'" class="f-center">
          <div class="f-spinner"></div>
          <p class="f-message">Connecting...</p>
        </div>

        <!-- Form -->
        <div v-else>
          <p v-if="integration?.description" class="f-desc">{{ integration.description }}</p>

          <!-- OAuth info -->
          <div v-if="authType === 'oauth2'" class="f-info">
            You'll be redirected to authorize the connection.
          </div>

          <!-- Dynamic fields -->
          <div v-for="field in fields" :key="field.key" class="f-field">
            <label :for="'f-' + field.key" class="f-label">
              {{ field.label }}<span v-if="field.required" class="f-req">*</span>
            </label>
            <input
              :id="'f-' + field.key"
              v-model="formData[field.key]"
              :type="field.type === 'secure' ? 'password' : field.type === 'url' ? 'url' : 'text'"
              :required="field.required"
              :placeholder="field.type === 'url' ? 'https://...' : field.type === 'secure' ? '••••••••' : ''"
              class="f-input"
            />
            <p v-if="field.description" class="f-hint">{{ field.description }}</p>
          </div>

          <!-- Label (always available) -->
          <div class="f-field">
            <label for="f-label" class="f-label">Label <span class="f-optional">(optional)</span></label>
            <input id="f-label" v-model="formData.label" type="text" placeholder="e.g. Work Account" class="f-input" />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="f-footer">
        <template v-if="state === 'completed' || state === 'error'">
          <button class="f-btn f-btn-primary" @click="close">Done</button>
        </template>
        <template v-else-if="state !== 'connecting'">
          <button class="f-btn" @click="close">Cancel</button>
          <button class="f-btn f-btn-primary" :disabled="!isValid" @click="handleConnect">
            {{ authType === 'oauth2' ? 'Authorize' : authType === 'oauth2-custom' ? 'Setup & Authorize' : 'Connect' }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'

const props = defineProps<{
  integrationId: string
  foundation?: any
}>()

const emit = defineEmits<{
  success: [data: { integration: any; configuration: any }]
  error: [data: { message: string }]
  close: []
}>()

const visible = ref(true)
const state = ref<'idle' | 'connecting' | 'completed' | 'error'>('idle')
const error = ref('')
const integration = ref<any>(null)
const formData = ref<Record<string, any>>({})

// Resolve Foundation instance
let sdk: any = null

onMounted(async () => {
  // Try to get Foundation instance from prop, window global, or attribute
  sdk = props.foundation || (window as any).__foundation
  if (!sdk) {
    error.value = 'Foundation SDK not available'
    state.value = 'error'
    return
  }

  // Load integration details
  try {
    const all = await sdk.integration.all()
    integration.value = all.find((i: any) => i.id === props.integrationId)
    if (!integration.value) {
      error.value = `Integration "${props.integrationId}" not found`
      state.value = 'error'
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load integration'
    state.value = 'error'
  }
})

const authType = computed(() => integration.value?.auth || integration.value?.authType || 'bearer')

const fields = computed(() => {
  if (!integration.value?.fields) return []
  return integration.value.fields.filter((f: any) => f.source === 'user.input')
})

const isValid = computed(() => {
  if (authType.value === 'oauth2') return true
  return fields.value.every((f: any) => {
    if (!f.required) return true
    return formData.value[f.key]?.toString().trim()
  })
})

function close() {
  visible.value = false
  emit('close')
}

async function handleConnect() {
  if (!sdk || !integration.value) return

  state.value = 'connecting'
  error.value = ''

  try {
    if (authType.value === 'oauth2') {
      // Standard OAuth — initialize, get URL, open popup
      const payload: Record<string, any> = {}
      if (formData.value.label) payload.label = formData.value.label

      const response = await sdk.integration.connect(props.integrationId)

      if (response?.url) {
        await openOAuthPopup(response.url)
        state.value = 'completed'
        emit('success', { integration: integration.value, configuration: response.configuration })
      } else if (response?.configuration?.connected) {
        state.value = 'completed'
        emit('success', { integration: integration.value, configuration: response.configuration })
      } else {
        throw new Error('Failed to get authorization URL')
      }

    } else if (authType.value === 'oauth2-custom') {
      // Two-step: setup credentials, then OAuth
      const setupPayload: Record<string, any> = {}
      if (formData.value.label) setupPayload.label = formData.value.label
      fields.value.forEach((f: any) => {
        if (formData.value[f.key]) setupPayload[f.key] = formData.value[f.key]
      })

      // Step 1: Setup
      const setupResponse = await fetch(getSetupUrl(), {
        method: 'POST',
        headers: await getHeaders(),
        body: JSON.stringify(setupPayload)
      })
      const setupData = await setupResponse.json()
      const setup = setupData.data ?? setupData

      if (!setup?.configuration?.id) throw new Error('Setup failed')

      // Step 2: Initialize with configuration ID
      const initResponse = await sdk.integration.connect(props.integrationId)

      if (initResponse?.url) {
        await openOAuthPopup(initResponse.url)
        state.value = 'completed'
        emit('success', { integration: integration.value, configuration: setup.configuration })
      } else {
        throw new Error('Failed to get authorization URL')
      }

    } else {
      // Bearer/custom — send credentials directly
      const payload: Record<string, any> = {}
      if (formData.value.label) payload.label = formData.value.label
      fields.value.forEach((f: any) => {
        if (formData.value[f.key]) payload[f.key] = formData.value[f.key]
      })

      const response = await sdk.integration.connect(props.integrationId)

      if (response?.configuration?.connected) {
        state.value = 'completed'
        emit('success', { integration: integration.value, configuration: response.configuration })
      } else if (response?.errors?.length) {
        throw new Error(response.errors.join(', '))
      } else {
        state.value = 'completed'
        emit('success', { integration: integration.value, configuration: response })
      }
    }
  } catch (err: any) {
    error.value = err.message || 'Connection failed'
    state.value = 'error'
    emit('error', { message: error.value })
  }
}

function getSetupUrl(): string {
  // Derive from SDK's config
  const baseUrl = sdk.config?.raw?.auth?.apiUrls?.apiBaseUrl || ''
  return `${baseUrl}/api/v1/accounts/integrations/${props.integrationId}/setup`
}

async function getHeaders(): Promise<Record<string, string>> {
  const token = await sdk.auth.getToken()
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

function openOAuthPopup(url: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const width = 600
    const height = 700
    const left = window.screenX + (window.outerWidth - width) / 2
    const top = window.screenY + (window.outerHeight - height) / 2

    const popup = window.open(url, 'foundation-oauth', `width=${width},height=${height},left=${left},top=${top}`)

    if (!popup) {
      reject(new Error('Popup blocked — please allow popups for this site'))
      return
    }

    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer)
        resolve()
      }
    }, 500)

    // Timeout after 3 minutes
    setTimeout(() => {
      clearInterval(timer)
      if (!popup.closed) popup.close()
      reject(new Error('Authorization timed out'))
    }, 180000)
  })
}
</script>

<style>
:host {
  all: initial;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.f-overlay {
  position: fixed; inset: 0; z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0,0,0,0.5);
}

.f-modal {
  background: white; border-radius: 12px; width: 100%; max-width: 420px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.15); overflow: hidden;
}

.f-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #e5e7eb;
}
.f-header-left { display: flex; align-items: center; gap: 10px; }
.f-logo { width: 32px; height: 32px; object-fit: contain; }
.f-title { font-size: 16px; font-weight: 600; color: #111827; }
.f-close {
  background: none; border: none; font-size: 24px; color: #9ca3af;
  cursor: pointer; padding: 0; line-height: 1;
}
.f-close:hover { color: #6b7280; }

.f-body { padding: 20px; max-height: 400px; overflow-y: auto; }

.f-center { text-align: center; padding: 24px 0; }
.f-check {
  width: 48px; height: 48px; border-radius: 50%; background: #dcfce7;
  color: #16a34a; font-size: 24px; display: inline-flex; align-items: center;
  justify-content: center; margin-bottom: 12px;
}
.f-error-icon {
  width: 48px; height: 48px; border-radius: 50%; background: #fee2e2;
  color: #dc2626; font-size: 24px; font-weight: bold; display: inline-flex;
  align-items: center; justify-content: center; margin-bottom: 12px;
}
.f-spinner {
  width: 32px; height: 32px; border: 3px solid #e5e7eb; border-top-color: #3b82f6;
  border-radius: 50%; animation: f-spin 0.7s linear infinite;
  margin: 0 auto 12px;
}
@keyframes f-spin { to { transform: rotate(360deg); } }
.f-message { color: #6b7280; font-size: 14px; }

.f-desc { color: #6b7280; font-size: 14px; margin-bottom: 16px; }

.f-info {
  background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 8px;
  padding: 12px; font-size: 13px; color: #1e40af; margin-bottom: 16px;
}

.f-field { margin-bottom: 14px; }
.f-label { display: block; font-size: 13px; font-weight: 500; color: #374151; margin-bottom: 4px; }
.f-req { color: #ef4444; }
.f-optional { color: #9ca3af; font-weight: 400; }
.f-input {
  width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px;
  font-size: 14px; color: #111827; background: white; box-sizing: border-box;
  outline: none;
}
.f-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 2px rgba(59,130,246,0.2); }
.f-hint { font-size: 12px; color: #9ca3af; margin-top: 4px; }

.f-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 14px 20px; background: #f9fafb; border-top: 1px solid #e5e7eb;
}

.f-btn {
  padding: 8px 16px; border-radius: 6px; font-size: 14px; font-weight: 500;
  cursor: pointer; border: 1px solid #d1d5db; background: white; color: #374151;
}
.f-btn:hover { background: #f3f4f6; }
.f-btn-primary { background: #3b82f6; color: white; border-color: #3b82f6; }
.f-btn-primary:hover { background: #2563eb; }
.f-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
